export type InvoiceMatchProduct = {
  id: string;
  product_code: string;
  product_name: string | null;
};

export type InvoiceMatchSource = {
  product_code?: unknown;
  product_name?: unknown;
};

export type InvoiceProductMatch = {
  product: InvoiceMatchProduct;
  confidence: number;
  method: "exact_product_code" | "exact_model_code" | "unique_model_code_prefix" | "exact_name" | "high_name_similarity";
};

export function normalizeInvoiceMatchText(value: unknown): string {
  return String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .replace(/[ıİ]/g, "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

const compact = (value: unknown) => normalizeInvoiceMatchText(value).replace(/\s/g, "");
const tokens = (value: unknown) => new Set(normalizeInvoiceMatchText(value).split(" ").filter((token) => token.length >= 3));

/** Extract supplier/Nike model identifiers without treating season or quantities as SKUs. */
export function invoiceModelCodes(value: unknown): string[] {
  const normalized = String(value ?? "").toLocaleUpperCase("tr-TR");
  return Array.from(new Set(normalized.match(/\b(?:[A-Z]{1,5}\d{3,}[A-Z0-9-]*|\d{6,})\b/g) ?? []));
}

/**
 * Resolve the product identity independently from its size/color variant.
 * A unique product can be cost-matched even when a stock variant still needs
 * selection. Ties and weak name-only matches are deliberately rejected.
 */
export function matchInvoiceProduct(
  source: InvoiceMatchSource,
  products: InvoiceMatchProduct[],
): InvoiceProductMatch | null {
  if (!products.length) return null;

  const sourceCode = compact(source.product_code);
  const sourceName = normalizeInvoiceMatchText(source.product_name);
  const sourceIdentity = [source.product_code, source.product_name].filter(Boolean).join(" ");
  // Detect the truncated suffix before extracting complete model codes: the
  // general extractor can otherwise interpret FZ9767- as the shorter FZ9767.
  const possibleTruncatedModelPrefix = String(sourceIdentity).toLocaleUpperCase("tr-TR")
    .match(/\b[A-Z]{1,5}\d{3,}-\s*$/)?.[0].replace(/\s/g, "") ?? null;
  const sourceModels = new Set(
    invoiceModelCodes(sourceIdentity).filter((model) =>
      !possibleTruncatedModelPrefix || model !== possibleTruncatedModelPrefix.replace(/-$/, ""),
    ),
  );
  // Some supplier PDFs truncate the final model identifier at a trailing hyphen
  // (e.g. FZ9767-). Only accept it if the full normalized description prefixes
  // a catalog name and the corresponding model prefix identifies one candidate.
  const truncatedModelPrefix = sourceModels.size === 0 ? possibleTruncatedModelPrefix : null;

  // Resolve repeated invoice descriptions against the full catalog before
  // model-code scoring. Some catalog internal codes look like model identifiers
  // even though the supplier description contains no model code.
  if (!sourceCode && sourceModels.size === 0 && sourceName) {
    const nameCandidates = products
      .map((product) => ({ product, name: normalizeInvoiceMatchText(product.product_name) }))
      .filter(({ name }) => name.length >= 12 && sourceName.includes(name));
    const longest = nameCandidates.reduce((winner, current) =>
      current.name.length > winner.name.length ? current : winner,
      nameCandidates[0],
    );
    if (longest && nameCandidates.length > 1 && nameCandidates.every(({ name }) =>
      name === longest.name || longest.name.startsWith(name + " "),
    )) {
      return {
        product: longest.product,
        confidence: Math.min(0.99, 0.82 + (longest.name.length / Math.max(sourceName.length, 1)) * 0.17),
        method: "high_name_similarity",
      };
    }
  }

  const scored = products.map((product) => {
    const productCode = compact(product.product_code);
    const productIdentity = [product.product_code, product.product_name].filter(Boolean).join(" ");
    const productModels = new Set(invoiceModelCodes(productIdentity));
    let score = 0;
    let method: InvoiceProductMatch["method"] = "high_name_similarity";

    if (sourceCode && sourceCode === productCode) {
      score = 1000;
      method = "exact_product_code";
    } else if (
      truncatedModelPrefix && sourceName &&
      normalizeInvoiceMatchText(product.product_name).startsWith(sourceName) &&
      [...productModels].some((model) => model.startsWith(truncatedModelPrefix))
    ) {
      score = 960;
      method = "unique_model_code_prefix";
    } else {
      const sharedModels = [...sourceModels].filter((model) => productModels.has(model));
      const conflictingModels = sourceModels.size > 0 && sharedModels.length === 0;
      if (sharedModels.length === 1) {
        score = 950;
        method = "exact_model_code";
      } else if (sharedModels.length > 1 || conflictingModels) {
        // Conflicting or multiple model identifiers are a hard stop; a similar
        // description must never override a known SKU/model-code conflict.
        score = 0;
      } else {
        const productName = normalizeInvoiceMatchText(product.product_name);
        if (sourceName && productName && sourceName === productName) {
          score = 900;
          method = "exact_name";
        } else if (sourceModels.size === 0 && sourceName && productName) {
          const sourceTokens = tokens(sourceName);
          const productTokens = tokens(productName);
          const common = [...sourceTokens].filter((token) => productTokens.has(token)).length;
          const overlap = sourceTokens.size ? common / sourceTokens.size : 0;
          const containment = sourceName.length >= 12 && productName.length >= 12 &&
            (sourceName.includes(productName) || productName.includes(sourceName));
          if (containment || overlap >= 0.92) {
            score = containment ? 820 : Math.round(700 + overlap * 100);
            method = "high_name_similarity";
          }
        }
      }
    }
    return { product, score, method };
  }).filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || a.product.product_code.localeCompare(b.product.product_code, "tr-TR"));

  if (!scored.length) return null;
  const best = scored[0];
  let tied = scored.filter((item) => item.score === best.score);
  if (best.method === "high_name_similarity") {
    // Repeated invoice descriptions can score the generic product name higher
    // than the full catalogue name. Prefer a uniquely most-specific catalogue
    // name only when all matching names are nested prefixes of that same name.
    const nameCandidates = products
      .map((product) => ({
        item: scored.find((candidate) => candidate.product.id === product.id),
        name: normalizeInvoiceMatchText(product.product_name),
      }))
      .filter((candidate): candidate is { item: (typeof scored)[number]; name: string } =>
        Boolean(candidate.item && candidate.item.method === "high_name_similarity" && candidate.name && sourceName.includes(candidate.name)),
      );
    const longest = nameCandidates.reduce((winner, current) =>
      current.name.length > winner.name.length ? current : winner,
      nameCandidates[0],
    );
    if (longest && nameCandidates.every(({ name }) =>
      name === longest.name || longest.name.startsWith(name + " "),
    )) {
      tied = [longest.item];
    }
  }
  if (tied.length !== 1) return null;

  return {
    product: best.product,
    confidence: best.method === "unique_model_code_prefix" ? 0.96 : best.score >= 950 ? 1 : best.score >= 900 ? 0.98 : best.score >= 820 ? 0.93 : Math.min(0.99, best.score / 1000),
    method: best.method,
  };
}
